import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Trophy, ShoppingBag, Users, FileText, Award, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPanel = () => {
  const { user } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [products, setProducts] = useState([]);
  const [championships, setChampionships] = useState([]);
  const [categories, setCategories] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [students, setStudents] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [emailSettings, setEmailSettings] = useState({ notification_email: '' });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('athletes');
  
  // Estados para imagens
  const [athleteImage, setAthleteImage] = useState('');
  const [productImage, setProductImage] = useState('');
  const [championshipImage, setChampionshipImage] = useState('');
  const [studentImage, setStudentImage] = useState('');
  
  // Estado para edição de aluno
  const [editingStudent, setEditingStudent] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [athletesRes, productsRes, championshipsRes, categoriesRes, enrollmentsRes, studentsRes, schedulesRes, appointmentsRes, emailRes] = await Promise.all([
        axios.get(`${API}/athletes`),
        axios.get(`${API}/products`),
        axios.get(`${API}/championships`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/admin/enrollments`),
        axios.get(`${API}/admin/students`),
        axios.get(`${API}/schedules`),
        axios.get(`${API}/admin/appointments`),
        axios.get(`${API}/admin/email-settings`)
      ]);
      
      setAthletes(athletesRes.data);
      setProducts(productsRes.data);
      setChampionships(championshipsRes.data);
      setCategories(categoriesRes.data);
      setEnrollments(enrollmentsRes.data);
      setStudents(studentsRes.data);
      setSchedules(schedulesRes.data);
      setAppointments(appointmentsRes.data);
      setEmailSettings(emailRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAthlete = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    // Usar a imagem do estado (upload ou URL)
    const photoUrl = athleteImage || formData.get('photo_url_url') || 'https://via.placeholder.com/400';
    
    const data = {
      name: formData.get('name'),
      photo_url: photoUrl,
      description: formData.get('description'),
      position: formData.get('position'),
      clubs_played: formData.get('clubs_played')
    };

    try {
      await axios.post(`${API}/admin/athletes`, data);
      toast.success('Atleta adicionado com sucesso!');
      fetchData();
      setAthleteImage('');
      e.target.reset();
    } catch (error) {
      console.error('Error adding athlete:', error);
      toast.error('Erro ao adicionar atleta');
    }
  };

  const handleDeleteAthlete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este atleta?')) return;
    
    try {
      await axios.delete(`${API}/admin/athletes/${id}`);
      toast.success('Atleta excluído com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Error deleting athlete:', error);
      toast.error('Erro ao excluir atleta');
    }
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const photoUrl = productImage || formData.get('photo_url_url') || 'https://via.placeholder.com/400';
    
    const data = {
      name: formData.get('name'),
      description: formData.get('description'),
      price: parseFloat(formData.get('price')),
      photo_url: photoUrl,
      category: formData.get('category'),
      stock: parseInt(formData.get('stock'))
    };

    try {
      await axios.post(`${API}/admin/products`, data);
      toast.success('Produto adicionado com sucesso!');
      fetchData();
      setProductImage('');
      e.target.reset();
    } catch (error) {
      console.error('Error adding product:', error);
      toast.error('Erro ao adicionar produto');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;
    
    try {
      await axios.delete(`${API}/admin/products/${id}`);
      toast.success('Produto excluído com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erro ao excluir produto');
    }
  };

  const handleAddChampionship = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const photoUrl = championshipImage || formData.get('photo_url_url') || 'https://via.placeholder.com/400';
    
    const data = {
      name: formData.get('name'),
      year: parseInt(formData.get('year')),
      location: formData.get('location'),
      photo_url: photoUrl,
      description: formData.get('description'),
      results: formData.get('results')
    };

    try {
      await axios.post(`${API}/admin/championships`, data);
      toast.success('Campeonato adicionado com sucesso!');
      fetchData();
      setChampionshipImage('');
      e.target.reset();
    } catch (error) {
      console.error('Error adding championship:', error);
      toast.error('Erro ao adicionar campeonato');
    }
  };

  const handleDeleteChampionship = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este campeonato?')) return;
    
    try {
      await axios.delete(`${API}/admin/championships/${id}`);
      toast.success('Campeonato excluído com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Error deleting championship:', error);
      toast.error('Erro ao excluir campeonato');
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      age_range: formData.get('age_range'),
      description: formData.get('description'),
      schedule: formData.get('schedule'),
      coach_name: formData.get('coach_name')
    };

    try {
      await axios.post(`${API}/admin/categories`, data);
      toast.success('Categoria adicionada com sucesso!');
      fetchData();
      e.target.reset();
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Erro ao adicionar categoria');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta categoria?')) return;
    
    try {
      await axios.delete(`${API}/admin/categories/${id}`);
      toast.success('Categoria excluída com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Erro ao excluir categoria');
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const photoUrl = studentImage || formData.get('photo_url_url') || null;
    
    const data = {
      name: formData.get('name'),
      birth_date: formData.get('birth_date'),
      category: formData.get('category'),
      parent_id: formData.get('parent_id') || 'parent-1',
      status: 'active',
      photo_url: photoUrl
    };

    try {
      await axios.post(`${API}/admin/students`, data);
      toast.success('Aluno adicionado com sucesso!');
      fetchData();
      setStudentImage('');
      e.target.reset();
    } catch (error) {
      console.error('Error adding student:', error);
      toast.error('Erro ao adicionar aluno');
    }
  };

  const handleDeleteStudent = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este aluno?')) return;
    
    try {
      await axios.delete(`${API}/admin/students/${id}`);
      toast.success('Aluno excluído com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Error deleting student:', error);
      toast.error('Erro ao excluir aluno');
    }
  };

  const handleEditStudent = (student) => {
    setEditingStudent(student);
    setStudentImage(student.photo_url || '');
  };

  const handleUpdateStudent = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const photoUrl = studentImage || formData.get('photo_url_url') || editingStudent.photo_url || null;
    
    const data = {
      name: formData.get('name'),
      birth_date: formData.get('birth_date'),
      category: formData.get('category'),
      photo_url: photoUrl
    };

    try {
      await axios.put(`${API}/admin/students/${editingStudent.id}`, data);
      toast.success('Aluno atualizado com sucesso!');
      setEditingStudent(null);
      setStudentImage('');
      fetchData();
    } catch (error) {
      console.error('Error updating student:', error);
      toast.error('Erro ao atualizar aluno');
    }
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const categoriesInput = formData.get('categories');
    const data = {
      day_of_week: formData.get('day_of_week'),
      time_slot: formData.get('time_slot'),
      categories: categoriesInput.split(',').map(c => c.trim()),
      location: formData.get('location'),
      notes: formData.get('notes')
    };

    try {
      await axios.post(`${API}/admin/schedules`, data);
      toast.success('Horário adicionado com sucesso!');
      fetchData();
      e.target.reset();
    } catch (error) {
      console.error('Error adding schedule:', error);
      toast.error('Erro ao adicionar horário');
    }
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este horário?')) return;
    
    try {
      await axios.delete(`${API}/admin/schedules/${id}`);
      toast.success('Horário excluído com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Erro ao excluir horário');
    }
  };

  const handleAddAppointment = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      visitor_name: formData.get('visitor_name'),
      visitor_email: formData.get('visitor_email'),
      visitor_phone: formData.get('visitor_phone'),
      appointment_date: formData.get('appointment_date'),
      appointment_time: formData.get('appointment_time'),
      appointment_type: formData.get('appointment_type'),
      notes: formData.get('notes')
    };

    try {
      await axios.post(`${API}/admin/appointments`, data);
      toast.success('Agendamento criado! E-mail de notificação enviado.');
      fetchData();
      e.target.reset();
    } catch (error) {
      console.error('Error adding appointment:', error);
      toast.error('Erro ao criar agendamento');
    }
  };

  const handleDeleteAppointment = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este agendamento?')) return;
    
    try {
      await axios.delete(`${API}/admin/appointments/${id}`);
      toast.success('Agendamento excluído com sucesso!');
      fetchData();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Erro ao excluir agendamento');
    }
  };

  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      notification_email: formData.get('notification_email')
    };

    try {
      await axios.put(`${API}/admin/email-settings`, data);
      toast.success('E-mail de notificação atualizado!');
      fetchData();
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error('Erro ao atualizar e-mail');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="animate-spin h-12 w-12 text-red-600" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:justify-between md:items-start gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="admin-panel-title">
              Painel Administrativo
            </h1>
            <p className="text-gray-600">Gerencie o conteúdo do site</p>
          </div>
          <Link to="/register">
            <Button className="bg-red-600 hover:bg-red-700">
              Registrar Novo Usuário
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6 flex items-center">
              <Award className="h-12 w-12 text-red-600 mr-4" />
              <div>
                <p className="text-gray-600">Ex-Atletas</p>
                <p className="text-3xl font-bold">{athletes.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center">
              <ShoppingBag className="h-12 w-12 text-red-600 mr-4" />
              <div>
                <p className="text-gray-600">Produtos</p>
                <p className="text-3xl font-bold">{products.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center">
              <Trophy className="h-12 w-12 text-red-600 mr-4" />
              <div>
                <p className="text-gray-600">Campeonatos</p>
                <p className="text-3xl font-bold">{championships.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center">
              <Users className="h-12 w-12 text-red-600 mr-4" />
              <div>
                <p className="text-gray-600">Alunos</p>
                <p className="text-3xl font-bold">{students.length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-8 gap-2">
            <TabsTrigger value="athletes" data-testid="athletes-tab">Ex-Atletas</TabsTrigger>
            <TabsTrigger value="products" data-testid="products-tab">Produtos</TabsTrigger>
            <TabsTrigger value="championships" data-testid="championships-tab">Campeonatos</TabsTrigger>
            <TabsTrigger value="categories" data-testid="categories-tab">Categorias</TabsTrigger>
            <TabsTrigger value="students" data-testid="students-tab">Alunos</TabsTrigger>
            <TabsTrigger value="schedules" data-testid="schedules-tab">Horários</TabsTrigger>
            <TabsTrigger value="agenda" data-testid="agenda-tab">
              Agenda ({appointments.filter(a => a.status === 'pending').length})
            </TabsTrigger>
            <TabsTrigger value="enrollments" data-testid="enrollments-tab">
              Inscrições ({enrollments.filter(e => e.status === 'pending').length})
            </TabsTrigger>
          </TabsList>

          {/* Athletes Tab */}
          <TabsContent value="athletes">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Add Form */}
              <Card>
                <CardHeader>
                  <CardTitle>Adicionar Ex-Atleta</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddAthlete} className="space-y-4">
                    <div>
                      <Label>Nome</Label>
                      <Input name="name" required data-testid="athlete-name-input" />
                    </div>
                    
                    <ImageUpload 
                      name="photo_url"
                      label="Foto do Atleta"
                      onImageChange={setAthleteImage}
                      currentImage={athleteImage}
                    />
                    
                    <div>
                      <Label>Posição</Label>
                      <Input name="position" data-testid="athlete-position-input" />
                    </div>
                    <div>
                      <Label>Clubes</Label>
                      <Input name="clubs_played" data-testid="athlete-clubs-input" />
                    </div>
                    <div>
                      <Label>Descrição</Label>
                      <Textarea name="description" rows={3} data-testid="athlete-description-input" />
                    </div>
                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="add-athlete-button">
                      Adicionar Atleta
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* List */}
              <Card>
                <CardHeader>
                  <CardTitle>Lista de Ex-Atletas ({athletes.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {athletes.map(athlete => (
                      <Card key={athlete.id} className="border">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div className="flex items-center">
                            <img src={athlete.photo_url} alt={athlete.name} className="w-12 h-12 rounded-full object-cover mr-3" />
                            <div>
                              <p className="font-semibold">{athlete.name}</p>
                              <p className="text-sm text-gray-500">{athlete.position}</p>
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            onClick={() => handleDeleteAthlete(athlete.id)}
                            data-testid={`delete-athlete-${athlete.id}`}
                          >
                            Excluir
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Adicionar Produto</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddProduct} className="space-y-4">
                    <div>
                      <Label>Nome</Label>
                      <Input name="name" required data-testid="product-name-input" />
                    </div>
                    <div>
                      <Label>Descrição</Label>
                      <Textarea name="description" required data-testid="product-description-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Preço (R$)</Label>
                        <Input name="price" type="number" step="0.01" required data-testid="product-price-input" />
                      </div>
                      <div>
                        <Label>Estoque</Label>
                        <Input name="stock" type="number" defaultValue="100" required data-testid="product-stock-input" />
                      </div>
                    </div>
                    <div>
                      <Label>Categoria</Label>
                      <Input name="category" required data-testid="product-category-input" />
                    </div>
                    
                    <ImageUpload 
                      name="photo_url"
                      label="Foto do Produto"
                      onImageChange={setProductImage}
                      currentImage={productImage}
                    />
                    
                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="add-product-button">
                      Adicionar Produto
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Lista de Produtos ({products.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {products.map(product => (
                      <Card key={product.id} className="border">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <p className="font-semibold">{product.name}</p>
                            <p className="text-red-600 font-bold">R$ {product.price.toFixed(2)}</p>
                            <p className="text-sm text-gray-500">Estoque: {product.stock}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            onClick={() => handleDeleteProduct(product.id)}
                            data-testid={`delete-product-${product.id}`}
                          >
                            Excluir
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Championships Tab */}
          <TabsContent value="championships">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Adicionar Campeonato</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddChampionship} className="space-y-4">
                    <div>
                      <Label>Nome</Label>
                      <Input name="name" required data-testid="championship-name-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Ano</Label>
                        <Input name="year" type="number" required data-testid="championship-year-input" />
                      </div>
                      <div>
                        <Label>Local</Label>
                        <Input name="location" required data-testid="championship-location-input" />
                      </div>
                    </div>
                    
                    <ImageUpload 
                      name="photo_url"
                      label="Logo/Foto do Campeonato"
                      onImageChange={setChampionshipImage}
                      currentImage={championshipImage}
                    />
                    
                    <div>
                      <Label>Descrição</Label>
                      <Textarea name="description" rows={2} data-testid="championship-description-input" />
                    </div>
                    <div>
                      <Label>Resultados</Label>
                      <Input name="results" data-testid="championship-results-input" />
                    </div>
                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="add-championship-button">
                      Adicionar Campeonato
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Lista de Campeonatos ({championships.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {championships.map(championship => (
                      <Card key={championship.id} className="border">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <p className="font-semibold">{championship.name}</p>
                            <p className="text-sm text-gray-500">{championship.year} - {championship.location}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            onClick={() => handleDeleteChampionship(championship.id)}
                            data-testid={`delete-championship-${championship.id}`}
                          >
                            Excluir
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Adicionar Categoria</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddCategory} className="space-y-4">
                    <div>
                      <Label>Nome</Label>
                      <Input name="name" required data-testid="category-name-input" />
                    </div>
                    <div>
                      <Label>Faixa Etária</Label>
                      <Input name="age_range" required data-testid="category-age-input" />
                    </div>
                    <div>
                      <Label>Descrição</Label>
                      <Textarea name="description" required data-testid="category-description-input" />
                    </div>
                    <div>
                      <Label>Horários</Label>
                      <Input name="schedule" required data-testid="category-schedule-input" />
                    </div>
                    <div>
                      <Label>Nome do Treinador</Label>
                      <Input name="coach_name" data-testid="category-coach-input" />
                    </div>
                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="add-category-button">
                      Adicionar Categoria
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Lista de Categorias ({categories.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {categories.map(category => (
                      <Card key={category.id} className="border">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <p className="font-semibold">{category.name}</p>
                            <p className="text-sm text-gray-500">{category.age_range}</p>
                          </div>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            onClick={() => handleDeleteCategory(category.id)}
                            data-testid={`delete-category-${category.id}`}
                          >
                            Excluir
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Enrollments Tab */}
          <TabsContent value="enrollments">
            <Card>
              <CardHeader>
                <CardTitle>Inscrições Recebidas ({enrollments.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {enrollments.map(enrollment => (
                    <Card key={enrollment.id} className="border">
                      <CardContent className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-gray-600">Aluno</Label>
                            <p className="font-semibold">{enrollment.student_name}</p>
                          </div>
                          <div>
                            <Label className="text-gray-600">Responsável</Label>
                            <p className="font-semibold">{enrollment.parent_name}</p>
                          </div>
                          <div>
                            <Label className="text-gray-600">Email</Label>
                            <p>{enrollment.email}</p>
                          </div>
                          <div>
                            <Label className="text-gray-600">Telefone</Label>
                            <p>{enrollment.phone}</p>
                          </div>
                          <div>
                            <Label className="text-gray-600">Data de Nascimento</Label>
                            <p>{enrollment.birth_date}</p>
                          </div>
                          <div>
                            <Label className="text-gray-600">Categoria</Label>
                            <p>{enrollment.category_interest}</p>
                          </div>
                          <div>
                            <Label className="text-gray-600">Tipo de Inscrição</Label>
                            <p className="font-semibold">
                              {enrollment.enrollment_type === 'aula_experimental' ? (
                                <span className="text-blue-600">🎯 Aula Experimental</span>
                              ) : (
                                <span className="text-green-600">⚽ Inscrição no Clube</span>
                              )}
                            </p>
                          </div>
                          {enrollment.message && (
                            <div className="md:col-span-2">
                              <Label className="text-gray-600">Mensagem</Label>
                              <p className="text-sm">{enrollment.message}</p>
                            </div>
                          )}
                          <div className="md:col-span-2">
                            <span className={`px-3 py-1 rounded-full text-sm ${
                              enrollment.status === 'approved' ? 'bg-green-100 text-green-800' :
                              enrollment.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              Status: {enrollment.status === 'pending' ? 'Pendente' : enrollment.status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Cadastrar Aluno</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddStudent} className="space-y-4">
                    <div>
                      <Label>Nome do Aluno</Label>
                      <Input name="name" required data-testid="student-name-input" />
                    </div>
                    <div>
                      <Label>Data de Nascimento</Label>
                      <Input name="birth_date" type="date" required data-testid="student-birthdate-input" />
                    </div>
                    <div>
                      <Label>Categoria</Label>
                      <Input name="category" required placeholder="Ex: Sub-9" data-testid="student-category-input" />
                    </div>
                    
                    <ImageUpload 
                      name="photo_url"
                      label="Foto do Aluno (Opcional)"
                      onImageChange={setStudentImage}
                      currentImage={studentImage}
                    />
                    
                    <div>
                      <Label>ID do Responsável</Label>
                      <Input name="parent_id" placeholder="parent-1" data-testid="student-parent-input" />
                      <p className="text-xs text-gray-500 mt-1">Deixe em branco para usar responsável padrão</p>
                    </div>
                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="add-student-button">
                      Cadastrar Aluno
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Lista de Alunos ({students.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {students.map(student => (
                      <Card key={student.id} className="border">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1">
                              <p className="font-semibold">{student.name}</p>
                              <p className="text-sm text-gray-600">{student.category}</p>
                              <p className="text-xs text-gray-500">Nasc: {student.birth_date}</p>
                              <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs ${
                                student.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {student.status === 'active' ? 'Ativo' : 'Inativo'}
                              </span>
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
                                onClick={() => handleEditStudent(student)}
                                data-testid={`edit-student-${student.id}`}
                              >
                                Editar
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive" 
                                onClick={() => handleDeleteStudent(student.id)}
                                data-testid={`delete-student-${student.id}`}
                              >
                                Excluir
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Modal de Edição */}
            {editingStudent && (
              <Dialog open={!!editingStudent} onOpenChange={() => setEditingStudent(null)}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Editar Aluno</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleUpdateStudent} className="space-y-4">
                    <div>
                      <Label>Nome do Aluno</Label>
                      <Input 
                        name="name" 
                        defaultValue={editingStudent.name}
                        required 
                        data-testid="edit-student-name-input" 
                      />
                    </div>
                    <div>
                      <Label>Data de Nascimento</Label>
                      <Input 
                        name="birth_date" 
                        type="date"
                        defaultValue={editingStudent.birth_date}
                        required 
                        data-testid="edit-student-birthdate-input" 
                      />
                    </div>
                    <div>
                      <Label>Categoria</Label>
                      <Input 
                        name="category" 
                        defaultValue={editingStudent.category}
                        required 
                        placeholder="Ex: Sub-9" 
                        data-testid="edit-student-category-input" 
                      />
                    </div>
                    
                    <ImageUpload 
                      name="photo_url"
                      label="Foto do Aluno (Opcional)"
                      onImageChange={setStudentImage}
                      currentImage={studentImage || editingStudent.photo_url}
                    />
                    
                    <div className="flex gap-3">
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          setEditingStudent(null);
                          setStudentImage('');
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        type="submit" 
                        className="flex-1 bg-blue-600 hover:bg-blue-700" 
                        data-testid="update-student-button"
                      >
                        Atualizar Aluno
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>

          {/* Schedules Tab */}
          <TabsContent value="schedules">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Adicionar Horário</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleAddSchedule} className="space-y-4">
                    <div>
                      <Label>Dia da Semana</Label>
                      <select name="day_of_week" className="w-full p-2 border rounded-md" required data-testid="schedule-day-select">
                        <option value="">Selecione...</option>
                        <option value="sunday">Domingo</option>
                        <option value="monday">Segunda</option>
                        <option value="tuesday">Terça</option>
                        <option value="wednesday">Quarta</option>
                        <option value="thursday">Quinta</option>
                        <option value="friday">Sexta</option>
                        <option value="saturday">Sábado</option>
                      </select>
                    </div>
                    <div>
                      <Label>Horário</Label>
                      <Input name="time_slot" required placeholder="Ex: 09:00-10:00" data-testid="schedule-time-input" />
                    </div>
                    <div>
                      <Label>Categorias</Label>
                      <Input name="categories" required placeholder="Ex: 2017, 2018, 2019" data-testid="schedule-categories-input" />
                      <p className="text-xs text-gray-500 mt-1">Separe por vírgula</p>
                    </div>
                    <div>
                      <Label>Local</Label>
                      <Input name="location" placeholder="Ex: Campo Principal" data-testid="schedule-location-input" />
                    </div>
                    <div>
                      <Label>Observações</Label>
                      <Textarea name="notes" rows={2} placeholder="Informações adicionais" data-testid="schedule-notes-input" />
                    </div>
                    <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="add-schedule-button">
                      Adicionar Horário
                    </Button>
                  </form>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Lista de Horários ({schedules.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {schedules.map(schedule => {
                      const dayNames = {
                        sunday: 'Domingo', monday: 'Segunda', tuesday: 'Terça',
                        wednesday: 'Quarta', thursday: 'Quinta', friday: 'Sexta', saturday: 'Sábado'
                      };
                      return (
                        <Card key={schedule.id} className="border">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-semibold">{dayNames[schedule.day_of_week]} - {schedule.time_slot}</p>
                                <p className="text-sm text-red-600 font-medium">{schedule.categories.join(', ')}</p>
                                {schedule.location && (
                                  <p className="text-xs text-gray-600">📍 {schedule.location}</p>
                                )}
                                {schedule.notes && (
                                  <p className="text-xs text-gray-500 mt-1">{schedule.notes}</p>
                                )}
                              </div>
                              <Button 
                                size="sm" 
                                variant="destructive" 
                                onClick={() => handleDeleteSchedule(schedule.id)}
                                data-testid={`delete-schedule-${schedule.id}`}
                              >
                                Excluir
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Agenda Tab */}
          <TabsContent value="agenda">
            <div className="space-y-6">
              {/* Email Configuration Card */}
              <Card className="border-blue-500">
                <CardHeader className="bg-blue-50">
                  <CardTitle className="flex items-center">
                    📧 Configuração de E-mail para Notificações
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <form onSubmit={handleUpdateEmail} className="space-y-4">
                    <div>
                      <Label>E-mail que receberá as notificações de agendamentos</Label>
                      <Input
                        name="notification_email"
                        type="email"
                        defaultValue={emailSettings.notification_email}
                        required
                        placeholder="pequeninos@pequeninos.com.br"
                        data-testid="notification-email-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Este e-mail receberá uma notificação sempre que um novo agendamento for criado
                      </p>
                    </div>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="update-email-button">
                      Atualizar E-mail
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Appointments Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Novo Agendamento</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleAddAppointment} className="space-y-4">
                      <div>
                        <Label>Nome do Visitante</Label>
                        <Input name="visitor_name" required data-testid="visitor-name-input" />
                      </div>
                      <div>
                        <Label>E-mail</Label>
                        <Input name="visitor_email" type="email" required data-testid="visitor-email-input" />
                      </div>
                      <div>
                        <Label>Telefone</Label>
                        <Input name="visitor_phone" type="tel" required data-testid="visitor-phone-input" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Data</Label>
                          <Input name="appointment_date" type="date" required data-testid="appointment-date-input" />
                        </div>
                        <div>
                          <Label>Horário</Label>
                          <Input name="appointment_time" type="time" required data-testid="appointment-time-input" />
                        </div>
                      </div>
                      <div>
                        <Label>Tipo de Agendamento</Label>
                        <select name="appointment_type" className="w-full p-2 border rounded-md" required data-testid="appointment-type-select">
                          <option value="aula_experimental">🎯 Aula Experimental</option>
                          <option value="visita_clube">🏟️ Visita ao Clube</option>
                        </select>
                      </div>
                      <div>
                        <Label>Observações</Label>
                        <Textarea name="notes" rows={2} placeholder="Informações adicionais..." data-testid="appointment-notes-input" />
                      </div>
                      <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" data-testid="add-appointment-button">
                        Criar Agendamento
                      </Button>
                      <p className="text-xs text-gray-500 text-center">
                        📧 Um e-mail será enviado para {emailSettings.notification_email || 'o e-mail configurado'}
                      </p>
                    </form>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Agendamentos ({appointments.length})</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {appointments.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">Nenhum agendamento</p>
                      ) : (
                        appointments.map(appointment => (
                          <Card key={appointment.id} className="border">
                            <CardContent className="p-4">
                              <div className="space-y-2">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <p className="font-semibold text-lg">{appointment.visitor_name}</p>
                                    <p className="text-sm text-gray-600">📧 {appointment.visitor_email}</p>
                                    <p className="text-sm text-gray-600">📱 {appointment.visitor_phone}</p>
                                  </div>
                                  <Button 
                                    size="sm" 
                                    variant="destructive" 
                                    onClick={() => handleDeleteAppointment(appointment.id)}
                                    data-testid={`delete-appointment-${appointment.id}`}
                                  >
                                    Excluir
                                  </Button>
                                </div>
                                <div className="border-t pt-2">
                                  <p className="text-sm font-semibold">
                                    📅 {new Date(appointment.appointment_date).toLocaleDateString('pt-BR')} às {appointment.appointment_time}
                                  </p>
                                  <p className="text-sm">
                                    {appointment.appointment_type === 'aula_experimental' ? (
                                      <span className="text-blue-600">🎯 Aula Experimental</span>
                                    ) : (
                                      <span className="text-green-600">🏟️ Visita ao Clube</span>
                                    )}
                                  </p>
                                  {appointment.notes && (
                                    <p className="text-xs text-gray-500 mt-1">💬 {appointment.notes}</p>
                                  )}
                                  <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs ${
                                    appointment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                    appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {appointment.status === 'pending' ? 'Pendente' :
                                     appointment.status === 'confirmed' ? 'Confirmado' : 'Cancelado'}
                                  </span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
};

export default AdminPanel;